import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export interface BigCommerceTranslations {
  title: string;
  description: string;
  setup: {
    title: string;
    description: string;
    storeHash: string;
    storeHashPlaceholder: string;
    storeHashHelp: string;
    accessToken: string;
    accessTokenPlaceholder: string;
    accessTokenHelp: string;
    testConnection: string;
    connectionSuccess: string;
    connectionFailed: string;
    saveConfiguration: string;
  };
  mapping: {
    title: string;
    description: string;
    kernlogicChannel: string;
    bigcommerceChannel: string;
    noChannelsFound: string;
    loadChannelsError: string;
    mappingSaved: string;
    mappingError: string;
  };
  sync: {
    title: string;
    syncNow: string;
    syncAll: string;
    syncSelected: string;
    syncInProgress: string;
    syncComplete: string;
    syncFailed: string;
    lastSync: string;
    never: string;
    productsSynced: string;
    productsSkipped: string;
    productsErrored: string;
  };
  status: {
    connected: string;
    disconnected: string;
    syncing: string;
    error: string;
    configured: string;
    notConfigured: string;
  };
  errors: {
    authenticationError: string;
    connectionFailed: string;
    permissionError: string;
    productSyncFailed: string;
    rateLimitExceeded: string;
    validationError: string;
    configurationError: string;
    channelMappingRequired: string;
    invalidStoreHash: string;
    invalidAccessToken: string;
    networkError: string;
    unknownError: string;
    genericError: string;
  };
  success: {
    configurationSaved: string;
    connectionEstablished: string;
    productSynced: string;
    channelAssigned: string;
    mappingUpdated: string;
  };
  warnings: {
    duplicateProduct: string;
    partialSync: string;
    channelNotMapped: string;
    missingRequiredFields: string;
  };
  buttons: {
    connect: string;
    disconnect: string;
    save: string;
    cancel: string;
    retry: string;
    configure: string;
    testConnection: string;
    syncNow: string;
    viewLogs: string;
    editMapping: string;
  };
  tooltips: {
    storeHash: string;
    accessToken: string;
    channelMapping: string;
    syncStatus: string;
    lastSyncTime: string;
  };
  placeholders: {
    selectChannel: string;
    noChannelSelected: string;
    searchChannels: string;
    enterStoreHash: string;
    enterAccessToken: string;
  };
  mappingFields: {
    sections: {
      basicInfo: {
        title: string;
        description: string;
      };
      dimensions: {
        title: string;
        description: string;
      };
      pricing: {
        title: string;
        description: string;
      };
      inventory: {
        title: string;
        description: string;
      };
      shipping: {
        title: string;
        description: string;
      };
      identifiers: {
        title: string;
        description: string;
      };
      variants: {
        title: string;
        description: string;
      };
      seo: {
        title: string;
        description: string;
      };
      categories: {
        title: string;
        description: string;
      };
      customFields: {
        title: string;
        description: string;
      };
      taxCompliance: {
        title: string;
        description: string;
      };
    };
    fields: {
      brandId: string;
      priceType: string;
      variantAttributes: string;
      taxClass: string;
      customFieldName: string;
      customField1: string;
      categoryMapping: string;
      weightUnit: string;
      mapPrice: string;
      inventoryTracking: string;
      optionExample: string;
      customField2: string;
      customField3: string;
      optionAttribute1: string;
      optionAttribute2: string;
      optionAttribute3: string;
      conditionValues: string;
    };
    labels: {
      storeHash: string;
      accessToken: string;
      productDescription: string;
      productType: string;
      searchKeywords: string;
      productCondition: string;
      brandId: string;
      productWeight: string;
      width: string;
      depth: string;
      height: string;
      basePriceType: string;
      costPrice: string;
      retailPrice: string;
      salePrice: string;
      mapPrice: string;
      stockQuantity: string;
      lowStockWarningLevel: string;
      trackInventory: string;
      binPickingNumber: string;
      fixedShippingCost: string;
      freeShipping: string;
      upcCode: string;
      gtin: string;
      mpn: string;
      isbn: string;
      ean: string;
      option1Name: string;
      option1Attribute: string;
      option2Name: string;
      option2Attribute: string;
      option3Name: string;
      option3Attribute: string;
      metaTitle: string;
      metaDescription: string;
      metaKeywords: string;
      pageTitle: string;
      taxClassId: string;
      customField1Name: string;
      customField1Attribute: string;
      customField2Name: string;
      customField2Attribute: string;
      customField3Name: string;
      customField3Attribute: string;
      autoCategoryMappingEnabled: string;
    };
  };
  channelMapping: {
    noKernlogicChannels: string;
    codeLabel: string;
    statusLabel: string;
    tipTitle: string;
    benefits: {
      syncSpecific: string;
      manageInventory: string;
      controlVisibility: string;
    };
    learnMore: string;
  };
}

export function useBigCommerceTranslations(): BigCommerceTranslations {
  const { t } = useTranslation('bigcommerce');

  // Memoize the translations object to ensure stable reference across renders
  return useMemo(
    () => ({
      title: t('title'),
      description: t('description'),
      setup: {
        title: t('setup.title'),
        description: t('setup.description'),
        storeHash: t('setup.storeHash'),
        storeHashPlaceholder: t('setup.storeHashPlaceholder'),
        storeHashHelp: t('setup.storeHashHelp'),
        accessToken: t('setup.accessToken'),
        accessTokenPlaceholder: t('setup.accessTokenPlaceholder'),
        accessTokenHelp: t('setup.accessTokenHelp'),
        testConnection: t('setup.testConnection'),
        connectionSuccess: t('setup.connectionSuccess'),
        connectionFailed: t('setup.connectionFailed'),
        saveConfiguration: t('setup.saveConfiguration'),
      },
      mapping: {
        title: t('mapping.title'),
        description: t('mapping.description'),
        kernlogicChannel: t('mapping.kernlogicChannel'),
        bigcommerceChannel: t('mapping.bigcommerceChannel'),
        noChannelsFound: t('mapping.noChannelsFound'),
        loadChannelsError: t('mapping.loadChannelsError'),
        mappingSaved: t('mapping.mappingSaved'),
        mappingError: t('mapping.mappingError'),
      },
      sync: {
        title: t('sync.title'),
        syncNow: t('sync.syncNow'),
        syncAll: t('sync.syncAll'),
        syncSelected: t('sync.syncSelected'),
        syncInProgress: t('sync.syncInProgress'),
        syncComplete: t('sync.syncComplete'),
        syncFailed: t('sync.syncFailed'),
        lastSync: t('sync.lastSync'),
        never: t('sync.never'),
        productsSynced: t('sync.productsSynced'),
        productsSkipped: t('sync.productsSkipped'),
        productsErrored: t('sync.productsErrored'),
      },
      status: {
        connected: t('status.connected'),
        disconnected: t('status.disconnected'),
        syncing: t('status.syncing'),
        error: t('status.error'),
        configured: t('status.configured'),
        notConfigured: t('status.notConfigured'),
      },
      errors: {
        authenticationError: t('errors.authenticationError'),
        connectionFailed: t('errors.connectionFailed'),
        permissionError: t('errors.permissionError'),
        productSyncFailed: t('errors.productSyncFailed'),
        rateLimitExceeded: t('errors.rateLimitExceeded'),
        validationError: t('errors.validationError'),
        configurationError: t('errors.configurationError'),
        channelMappingRequired: t('errors.channelMappingRequired'),
        invalidStoreHash: t('errors.invalidStoreHash'),
        invalidAccessToken: t('errors.invalidAccessToken'),
        networkError: t('errors.networkError'),
        unknownError: t('errors.unknownError'),
        genericError: t('errors.genericError'),
      },
      success: {
        configurationSaved: t('success.configurationSaved'),
        connectionEstablished: t('success.connectionEstablished'),
        productSynced: t('success.productSynced'),
        channelAssigned: t('success.channelAssigned'),
        mappingUpdated: t('success.mappingUpdated'),
      },
      warnings: {
        duplicateProduct: t('warnings.duplicateProduct'),
        partialSync: t('warnings.partialSync'),
        channelNotMapped: t('warnings.channelNotMapped'),
        missingRequiredFields: t('warnings.missingRequiredFields'),
      },
      buttons: {
        connect: t('buttons.connect'),
        disconnect: t('buttons.disconnect'),
        save: t('buttons.save'),
        cancel: t('buttons.cancel'),
        retry: t('buttons.retry'),
        configure: t('buttons.configure'),
        testConnection: t('buttons.testConnection'),
        syncNow: t('buttons.syncNow'),
        viewLogs: t('buttons.viewLogs'),
        editMapping: t('buttons.editMapping'),
      },
      tooltips: {
        storeHash: t('tooltips.storeHash'),
        accessToken: t('tooltips.accessToken'),
        channelMapping: t('tooltips.channelMapping'),
        syncStatus: t('tooltips.syncStatus'),
        lastSyncTime: t('tooltips.lastSyncTime'),
      },
      placeholders: {
        selectChannel: t('placeholders.selectChannel'),
        noChannelSelected: t('placeholders.noChannelSelected'),
        searchChannels: t('placeholders.searchChannels'),
        enterStoreHash: t('placeholders.enterStoreHash'),
        enterAccessToken: t('placeholders.enterAccessToken'),
      },
      mappingFields: {
        sections: {
          basicInfo: {
            title: t('mappingFields.sections.basicInfo.title'),
            description: t('mappingFields.sections.basicInfo.description'),
          },
          dimensions: {
            title: t('mappingFields.sections.dimensions.title'),
            description: t('mappingFields.sections.dimensions.description'),
          },
          pricing: {
            title: t('mappingFields.sections.pricing.title'),
            description: t('mappingFields.sections.pricing.description'),
          },
          inventory: {
            title: t('mappingFields.sections.inventory.title'),
            description: t('mappingFields.sections.inventory.description'),
          },
          shipping: {
            title: t('mappingFields.sections.shipping.title'),
            description: t('mappingFields.sections.shipping.description'),
          },
          identifiers: {
            title: t('mappingFields.sections.identifiers.title'),
            description: t('mappingFields.sections.identifiers.description'),
          },
          variants: {
            title: t('mappingFields.sections.variants.title'),
            description: t('mappingFields.sections.variants.description'),
          },
          seo: {
            title: t('mappingFields.sections.seo.title'),
            description: t('mappingFields.sections.seo.description'),
          },
          categories: {
            title: t('mappingFields.sections.categories.title'),
            description: t('mappingFields.sections.categories.description'),
          },
          customFields: {
            title: t('mappingFields.sections.customFields.title'),
            description: t('mappingFields.sections.customFields.description'),
          },
          taxCompliance: {
            title: t('mappingFields.sections.taxCompliance.title'),
            description: t('mappingFields.sections.taxCompliance.description'),
          },
        },
        fields: {
          brandId: t('mappingFields.fields.brandId'),
          priceType: t('mappingFields.fields.priceType'),
          variantAttributes: t('mappingFields.fields.variantAttributes'),
          taxClass: t('mappingFields.fields.taxClass'),
          customFieldName: t('mappingFields.fields.customFieldName'),
          customField1: t('mappingFields.fields.customField1'),
          categoryMapping: t('mappingFields.fields.categoryMapping'),
          weightUnit: t('mappingFields.fields.weightUnit'),
          mapPrice: t('mappingFields.fields.mapPrice'),
          inventoryTracking: t('mappingFields.fields.inventoryTracking'),
          optionExample: t('mappingFields.fields.optionExample'),
          customField2: t('mappingFields.fields.customField2'),
          customField3: t('mappingFields.fields.customField3'),
          optionAttribute1: t('mappingFields.fields.optionAttribute1'),
          optionAttribute2: t('mappingFields.fields.optionAttribute2'),
          optionAttribute3: t('mappingFields.fields.optionAttribute3'),
          conditionValues: t('mappingFields.fields.conditionValues'),
        },
        labels: {
          storeHash: t('mappingFields.labels.storeHash'),
          accessToken: t('mappingFields.labels.accessToken'),
          productDescription: t('mappingFields.labels.productDescription'),
          productType: t('mappingFields.labels.productType'),
          searchKeywords: t('mappingFields.labels.searchKeywords'),
          productCondition: t('mappingFields.labels.productCondition'),
          brandId: t('mappingFields.labels.brandId'),
          productWeight: t('mappingFields.labels.productWeight'),
          width: t('mappingFields.labels.width'),
          depth: t('mappingFields.labels.depth'),
          height: t('mappingFields.labels.height'),
          basePriceType: t('mappingFields.labels.basePriceType'),
          costPrice: t('mappingFields.labels.costPrice'),
          retailPrice: t('mappingFields.labels.retailPrice'),
          salePrice: t('mappingFields.labels.salePrice'),
          mapPrice: t('mappingFields.labels.mapPrice'),
          stockQuantity: t('mappingFields.labels.stockQuantity'),
          lowStockWarningLevel: t('mappingFields.labels.lowStockWarningLevel'),
          trackInventory: t('mappingFields.labels.trackInventory'),
          binPickingNumber: t('mappingFields.labels.binPickingNumber'),
          fixedShippingCost: t('mappingFields.labels.fixedShippingCost'),
          freeShipping: t('mappingFields.labels.freeShipping'),
          upcCode: t('mappingFields.labels.upcCode'),
          gtin: t('mappingFields.labels.gtin'),
          mpn: t('mappingFields.labels.mpn'),
          isbn: t('mappingFields.labels.isbn'),
          ean: t('mappingFields.labels.ean'),
          option1Name: t('mappingFields.labels.option1Name'),
          option1Attribute: t('mappingFields.labels.option1Attribute'),
          option2Name: t('mappingFields.labels.option2Name'),
          option2Attribute: t('mappingFields.labels.option2Attribute'),
          option3Name: t('mappingFields.labels.option3Name'),
          option3Attribute: t('mappingFields.labels.option3Attribute'),
          metaTitle: t('mappingFields.labels.metaTitle'),
          metaDescription: t('mappingFields.labels.metaDescription'),
          metaKeywords: t('mappingFields.labels.metaKeywords'),
          pageTitle: t('mappingFields.labels.pageTitle'),
          taxClassId: t('mappingFields.labels.taxClassId'),
          customField1Name: t('mappingFields.labels.customField1Name'),
          customField1Attribute: t('mappingFields.labels.customField1Attribute'),
          customField2Name: t('mappingFields.labels.customField2Name'),
          customField2Attribute: t('mappingFields.labels.customField2Attribute'),
          customField3Name: t('mappingFields.labels.customField3Name'),
          customField3Attribute: t('mappingFields.labels.customField3Attribute'),
          autoCategoryMappingEnabled: t('mappingFields.labels.autoCategoryMappingEnabled'),
        },
      },
      channelMapping: {
        noKernlogicChannels: t('channelMapping.noKernlogicChannels'),
        codeLabel: t('channelMapping.codeLabel'),
        statusLabel: t('channelMapping.statusLabel'),
        tipTitle: t('channelMapping.tipTitle'),
        benefits: {
          syncSpecific: t('channelMapping.benefits.syncSpecific'),
          manageInventory: t('channelMapping.benefits.manageInventory'),
          controlVisibility: t('channelMapping.benefits.controlVisibility'),
        },
        learnMore: t('channelMapping.learnMore'),
      },
    }),
    [t]
  );
}
